import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  SparklesIcon,
  LightBulbIcon,
  TrophyIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  BoltIcon,
  StarIcon
} from '@heroicons/react/24/outline'
import { api } from '../services/api'
import { useAuth } from '../contexts/AuthContext'

const AIInsights = () => {
  const [loading, setLoading] = useState(true)
  const [insights, setInsights] = useState(null)
  const [error, setError] = useState(null)
  const [trades, setTrades] = useState([])
  const { user } = useAuth()

  useEffect(() => {
    const fetchInsights = async () => {
      try {
        setLoading(true)
        setError(null)

        // Fetch user's trades
        const tradesResponse = await api.trades.getAll({ status: 'closed', limit: 100 })
        const userTrades = tradesResponse.data.trades || []
        setTrades(userTrades)

        // Fetch AI insights if available
        let aiInsights = null
        try {
          const insightsResponse = await api.ai.getInsights()
          aiInsights = insightsResponse.data
        } catch (aiError) {
          console.log('AI insights not available, generating from trades data')
        }

        if (userTrades.length === 0) {
          setInsights({
            overallScore: 0,
            totalPnL: 0,
            winRate: 0,
            tradesAnalyzed: 0,
            strengths: ['No trades to analyze yet', 'Ready to start your trading journey', 'AI will learn from your trading patterns'],
            weaknesses: ['Start trading to get AI insights', 'No trading history available', 'Add trades to unlock personalized analysis'],
            recommendations: ['Add your first trade to begin analysis', 'Connect a broker for automatic trade import', 'Start with paper trading to build confidence']
          })
        } else {
          // Generate insights from user's actual trades data
          const totalPnL = userTrades.reduce((sum, trade) => sum + (trade.pnl || 0), 0)
          const winningTrades = userTrades.filter(trade => (trade.pnl || 0) > 0).length
          const winRate = userTrades.length > 0 ? (winningTrades / userTrades.length) * 100 : 0
          const avgWin = winningTrades > 0 ? userTrades.filter(t => (t.pnl || 0) > 0).reduce((sum, t) => sum + t.pnl, 0) / winningTrades : 0
          const losingTrades = userTrades.filter(t => (t.pnl || 0) < 0)
          const avgLoss = losingTrades.length > 0 ? Math.abs(losingTrades.reduce((sum, t) => sum + t.pnl, 0) / losingTrades.length) : 0

          const bestTrade = userTrades.reduce((best, trade) => (trade.pnl || 0) > (best.pnl || 0) ? trade : best, userTrades[0])
          const worstTrade = userTrades.reduce((worst, trade) => (trade.pnl || 0) < (worst.pnl || 0) ? trade : worst, userTrades[0])

          // Get most traded symbols
          const symbolCounts = userTrades.reduce((acc, trade) => {
            acc[trade.symbol] = (acc[trade.symbol] || 0) + 1
            return acc
          }, {})
          const mostTradedSymbol = Object.entries(symbolCounts).sort(([,a], [,b]) => b - a)[0]?.[0]

          setInsights({
            overallScore: Math.min(100, Math.max(0, Math.round(winRate + (totalPnL > 0 ? 20 : -10)))),
            totalPnL,
            winRate: Math.round(winRate),
            tradesAnalyzed: userTrades.length,
            strengths: [
              winningTrades > 0 ? `${winningTrades} winning trades with avg profit of $${avgWin.toFixed(2)}` : 'Consistent trading activity',
              bestTrade ? `Best trade: ${bestTrade.symbol} (+$${(bestTrade.pnl || 0).toFixed(2)})` : 'Building trading experience',
              winRate > 60 ? "Strong win rate discipline" : winRate > 40 ? "Balanced trading approach" : "Learning from experience",
              mostTradedSymbol ? `Most active in ${mostTradedSymbol} (${symbolCounts[mostTradedSymbol]} trades)` : 'Diversified trading approach'
            ],
            weaknesses: [
              losingTrades.length > 0 ? `${losingTrades.length} losing trades with avg loss of $${avgLoss.toFixed(2)}` : "No significant losses detected",
              worstTrade && worstTrade.pnl < 0 ? `Worst trade: ${worstTrade.symbol} ($${worstTrade.pnl.toFixed(2)})` : "Risk management needs attention",
              winRate < 50 ? "Win rate below 50% - review strategy" : "Position sizing could be optimized",
              avgLoss > avgWin ? "Average losses exceed average wins" : "Consider tighter stop losses"
            ],
            recommendations: [
              totalPnL > 0 ? "Continue current strategy - showing profits" : "Review risk management approach",
              bestTrade ? `Analyze ${bestTrade.symbol} trade setup for replication` : "Focus on consistent trade documentation",
              winRate < 50 ? "Consider tighter stop losses and better entry timing" : "Look for opportunities to increase position sizes on high-confidence trades",
              mostTradedSymbol ? `Consider diversifying beyond ${mostTradedSymbol}` : "Maintain current diversification strategy"
            ]
          })
        }

        setLoading(false)
      } catch (err) {
        console.error('Error fetching insights:', err)
        setError('Failed to load AI insights. Please try again.')
        setLoading(false)
      }
    }

    if (user) {
      fetchInsights()
    }
  }, [user])

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <motion.div 
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-2xl mb-4 shadow-2xl">
              <SparklesIcon className="w-8 h-8 text-white animate-pulse" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">AI is analyzing your trades...</h2>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-gray-300">This may take a few moments</p>
        </motion.div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-4">Error Loading Insights</h2>
          <p className="text-gray-300 mb-6">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-purple-600 text-white font-semibold rounded-xl"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-cyan-500 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-pulse"></div>
      </div>

      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="relative max-w-7xl mx-auto"
      >
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-xl shadow-lg">
              <SparklesIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-white via-cyan-200 to-purple-200 bg-clip-text text-transparent">
                AI Trading Insights
              </h1>
              <p className="text-gray-300 mt-1">
                Analyzed {insights?.tradesAnalyzed || 0} trades • Total P&L: ${insights?.totalPnL?.toFixed(2) || '0.00'}
              </p>
            </div>
          </div>
        </div>

        {/* Performance Score */}
        <div className="mb-8">
          <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-white mb-6">Trading Performance Score</h2>
              <div className="relative inline-flex items-center justify-center">
                <svg className="w-32 h-32 transform -rotate-90">
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    stroke="rgba(255,255,255,0.1)"
                    strokeWidth="8"
                    fill="none"
                  />
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    stroke="url(#gradient)"
                    strokeWidth="8"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 56}`}
                    strokeDashoffset={`${2 * Math.PI * 56 * (1 - (insights?.overallScore || 0) / 100)}`}
                    className="transition-all duration-1000 ease-out"
                  />
                  <defs>
                    <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#06b6d4" />
                      <stop offset="100%" stopColor="#8b5cf6" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-white">{insights?.overallScore || 0}</div>
                    <div className="text-sm text-gray-300">out of 100</div>
                  </div>
                </div>
              </div>
              <p className="text-gray-300 mt-4">
                {insights?.overallScore > 70 ? 'Excellent performance!' : 
                 insights?.overallScore > 50 ? 'Good performance with room for improvement' :
                 insights?.overallScore > 0 ? 'Building your trading skills' : 'Ready to start your trading journey'}
              </p>
            </div>
          </div>
        </div>

        {/* Insights Grid */}
        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          {/* Strengths */}
          <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl">
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                <TrophyIcon className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white">Your Strengths</h3>
            </div>
            <div className="space-y-4">
              {insights?.strengths.map((strength, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-3 p-3 bg-green-500/10 border border-green-500/20 rounded-xl"
                >
                  <StarIcon className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span className="text-gray-200">{strength}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Areas for Improvement */}
          <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl">
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-orange-500 to-red-600 rounded-xl flex items-center justify-center">
                <ExclamationTriangleIcon className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white">Areas for Improvement</h3>
            </div>
            <div className="space-y-4">
              {insights?.weaknesses.map((weakness, index) => (
                <div
                  key={index}
                  className="flex items-center space-x-3 p-3 bg-orange-500/10 border border-orange-500/20 rounded-xl"
                >
                  <EyeIcon className="w-5 h-5 text-orange-400 flex-shrink-0" />
                  <span className="text-gray-200">{weakness}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* AI Recommendations */}
        <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl mb-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-xl flex items-center justify-center">
              <LightBulbIcon className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-xl font-bold text-white">AI Recommendations</h3>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {insights?.recommendations.map((recommendation, index) => (
              <div
                key={index}
                className="p-4 bg-gradient-to-r from-cyan-500/10 to-purple-600/10 border border-cyan-500/20 rounded-xl hover:border-cyan-500/40 transition-all duration-200"
              >
                <div className="flex items-start space-x-3">
                  <BoltIcon className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-200 text-sm">{recommendation}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Analyzed Trades */}
        {trades.length > 0 && (
          <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl mb-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                <i className="fas fa-database text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-white">Recent Analyzed Trades</h3>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {trades.slice(0, 6).map((trade, index) => (
                <div key={trade.id || index} className={`p-4 border rounded-xl ${
                  (trade.pnl || 0) > 0 ? 'bg-green-500/10 border-green-500/20' :
                  (trade.pnl || 0) < 0 ? 'bg-red-500/10 border-red-500/20' :
                  'bg-blue-500/10 border-blue-500/20'
                }`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-white font-semibold">{trade.symbol}</div>
                      <div className="text-xs text-gray-300">
                        {trade.side?.toUpperCase()} • {trade.quantity} shares
                      </div>
                    </div>
                    <div className={`font-bold ${
                      (trade.pnl || 0) > 0 ? 'text-green-400' :
                      (trade.pnl || 0) < 0 ? 'text-red-400' :
                      'text-blue-400'
                    }`}>
                      {trade.pnl ? `${trade.pnl > 0 ? '+' : ''}$${trade.pnl.toFixed(2)}` : 'OPEN'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </motion.div>
    </div>
  )
}

export default AIInsights